import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Star, CheckCircle2 } from "lucide-react";
import { apiErrorMessage } from "@/lib/apiError";

/**
 * Iter 253q · Public feedback form.
 *
 * Renders on `/feedback/:token` (unauthenticated). Fetches the request via
 * the magic-link, shows all target team members with 1-5-star inputs per
 * parameter + a comment box each, plus an overall comment. Submits once
 * and shows a thank-you screen.
 */
export default function PublicFeedback() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [overall, setOverall] = useState("");
  const [ratings, setRatings] = useState({});  // {user_id: {param: rating}}
  const [comments, setComments] = useState({}); // {user_id: comment}

  useEffect(() => {
    api.get(`/public/feedback/${token}`).then((r) => {
      setData(r.data);
      setEmail(r.data.request?.client_email || "");
      setName(r.data.request?.client_name || "");
    }).catch((e) => {
      setError(e?.response?.data?.detail || "Feedback request not found");
    });
  }, [token]);

  const setRating = (uid, param, val) => {
    setRatings((s) => ({ ...s, [uid]: { ...(s[uid] || {}), [param]: val } }));
  };
  const setComment = (uid, val) => setComments((s) => ({ ...s, [uid]: val }));

  const submit = async () => {
    setBusy(true);
    try {
      const per_user = (data.targets || []).map((u) => ({
        user_id: u.id,
        ratings: ratings[u.id] || {},
        comment: comments[u.id] || null,
      }));
      await api.post(`/public/feedback/${token}/submit`, {
        per_user,
        overall_comment: overall || null,
        submitted_by_name: name || null,
        submitted_by_email: email || null,
      });
      setSubmitted(true);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Submit failed"));
    } finally { setBusy(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md text-center border border-border p-8" data-testid="feedback-public-error">
          <div className="text-2xl font-bold mb-2">Can't open this feedback form</div>
          <div className="text-sm text-muted-foreground">{error}</div>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="p-10 text-center text-muted-foreground">Loading…</div>;
  }
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md text-center border border-border p-10" data-testid="feedback-public-thanks">
          <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-500 mb-4" />
          <div className="text-2xl font-bold mb-2">Thank you!</div>
          <div className="text-sm text-muted-foreground">Your feedback has been recorded. The team has been notified.</div>
        </div>
      </div>
    );
  }

  const req = data.request;
  const targets = data.targets || [];
  const ctx = data.context || {};

  return (
    <div className="min-h-screen bg-background text-foreground py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6" data-testid="feedback-public-form">
        <header className="space-y-1">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Feedback Request</div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: "var(--font-heading, inherit)" }}>{req.title}</h1>
          {req.description && <p className="text-sm text-muted-foreground">{req.description}</p>}
          {(ctx.brand || ctx.project) && (
            <div className="text-xs text-muted-foreground">
              {ctx.brand ? `Brand: ${ctx.brand.name}` : `Project: ${ctx.project?.name}`}
            </div>
          )}
        </header>

        <section className="border border-border p-4 space-y-2">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Your details</div>
          <div className="grid grid-cols-2 gap-3">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
                   className="rounded-none" data-testid="feedback-public-name" />
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email"
                   className="rounded-none" data-testid="feedback-public-email" />
          </div>
        </section>

        {targets.map((u) => (
          <section key={u.id} className="border border-border p-4 space-y-3" data-testid={`feedback-user-${u.id}`}>
            <div className="flex items-center gap-3">
              {u.avatar_url ? (
                <img src={u.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                  {(u.name || u.email || "?").slice(0, 1).toUpperCase()}
                </div>
              )}
              <div>
                <div className="font-semibold">{u.name || "Team member"}</div>
                <div className="text-xs text-muted-foreground">{u.email}</div>
              </div>
            </div>
            <div className="space-y-2">
              {(req.parameters || []).map((p) => {
                const rating = ratings[u.id]?.[p.name] || 0;
                return (
                  <div key={p.name} className="flex items-center gap-3">
                    <span className="text-sm flex-1">{p.name}</span>
                    <div className="flex gap-0.5" data-testid={`feedback-stars-${u.id}-${p.name}`}>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          type="button"
                          key={n}
                          onClick={() => setRating(u.id, p.name, n)}
                          className="p-0.5 hover:scale-110 transition"
                          data-testid={`feedback-star-${u.id}-${p.name}-${n}`}
                        >
                          <Star className={`w-5 h-5 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground"}`} />
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <Textarea rows={2} value={comments[u.id] || ""} onChange={(e) => setComment(u.id, e.target.value)}
                      placeholder={`Comments for ${u.name || "team member"}…`}
                      className="rounded-none" data-testid={`feedback-comment-${u.id}`} />
          </section>
        ))}

        <section className="border border-border p-4 space-y-2">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Overall comment</div>
          <Textarea rows={3} value={overall} onChange={(e) => setOverall(e.target.value)}
                    placeholder="Any general comments about the team's work…"
                    className="rounded-none" data-testid="feedback-overall" />
        </section>

        <Button className="w-full h-11 rounded-none" onClick={submit} disabled={busy} data-testid="feedback-public-submit">
          {busy ? "Submitting…" : "Submit feedback"}
        </Button>
      </div>
    </div>
  );
}
